import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BookOpen,
  Home,
  Palette,
  RotateCcw,
  Save,
  Trophy,
} from 'lucide-react';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { Input } from '@/components/ui/Input';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { contrastRatio, isDarkColor } from '@/hooks/useApplyUITheme';
import { BUSINESS_SETTINGS_PERMISSION } from '@/constants/constant-permissions';
import { extractErrorMessage } from '@/utils/error-helper';
import {
  DEFAULT_SITE_THEME,
  SiteTheme,
  THEME_PRESETS,
  UICustomization as UICustomizationData,
  UISite as Site,
  uiCustomizationService,
} from '@/services/ui-customization.service';

type ThemeField = keyof SiteTheme;
type FormSite = Required<SiteTheme>;
type FormValues = Record<Site, FormSite>;

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const SITE_TABS: { key: Site; label: string }[] = [
  { key: 'admin', label: 'Admin UI' },
  { key: 'learner', label: 'Learner UI' },
  { key: 'auth', label: 'Authentication UI' },
];

// Settings grouped by role, the way theme editors in most products are organised
const COLOR_GROUPS: {
  title: string;
  fields: { key: ThemeField; label: string; description: string }[];
}[] = [
  {
    title: 'Brand',
    fields: [
      {
        key: 'primary_color',
        label: 'Primary Color',
        description:
          'Buttons, links, selected tabs, progress bars and page headers. Hover and light shades are generated.',
      },
    ],
  },
  {
    title: 'Sidebar',
    fields: [
      {
        key: 'sidebar_background',
        label: 'Sidebar Background',
        description: 'The menu panel',
      },
      {
        key: 'sidebar_text',
        label: 'Sidebar Text',
        description: 'Menu labels and icons. Empty picks a readable color.',
      },
      {
        key: 'sidebar_active',
        label: 'Selected Item',
        description: 'Highlighted menu item. Empty uses the primary color.',
      },
    ],
  },
  {
    title: 'Page',
    fields: [
      {
        key: 'page_background',
        label: 'Page Background',
        description: 'Area behind the page content',
      },
    ],
  },
];

const EMPTY_SITE: FormSite = {
  primary_color: '',
  sidebar_background: '',
  sidebar_text: '',
  sidebar_active: '',
  page_background: '',
};

// Form keeps every field as a string ('' = default) so inputs stay controlled
const toFormSite = (site?: SiteTheme): FormSite => ({
  ...EMPTY_SITE,
  ...(site || {}),
});

// Send only the fields that are set; the backend treats missing fields as "default"
const toPayloadSite = (site?: FormSite): SiteTheme => {
  if (!site) return {};
  return Object.fromEntries(
    Object.entries(site).filter(([, value]) => value && typeof value === 'string' && value.trim() !== ''),
  ) as SiteTheme;
};

const validHex = (value?: string) =>
  value && HEX_COLOR.test(value) ? value : '';

// Resolves what the site will actually look like, filling empty fields the same way the live theme does
const resolveTheme = (values: FormSite) => {
  const sidebarBg =
    validHex(values.sidebar_background) ||
    DEFAULT_SITE_THEME.sidebar_background;
  const primary =
    validHex(values.primary_color) || DEFAULT_SITE_THEME.primary_color;
  return {
    primary,
    sidebarBg,
    sidebarText:
      validHex(values.sidebar_text) ||
      (validHex(values.sidebar_background)
        ? isDarkColor(sidebarBg)
          ? '#e5e7eb'
          : '#4b5563'
        : DEFAULT_SITE_THEME.sidebar_text),
    sidebarActive: validHex(values.sidebar_active) || primary,
    pageBg:
      validHex(values.page_background) || DEFAULT_SITE_THEME.page_background,
  };
};

// Readability checks (WCAG AA: 4.5 for text, 3 for large/UI elements)
const contrastWarnings = (theme: ReturnType<typeof resolveTheme>) => {
  const warnings: string[] = [];
  if (contrastRatio(theme.primary, '#ffffff') < 4.5)
    warnings.push(
      'White text on the primary color may be hard to read. Pick a darker primary color.',
    );
  if (contrastRatio(theme.sidebarText, theme.sidebarBg) < 4.5)
    warnings.push(
      'Sidebar text may be hard to read on the sidebar background.',
    );
  if (contrastRatio(theme.sidebarActive, theme.sidebarBg) < 3)
    warnings.push(
      'The selected item color is close to the sidebar background.',
    );
  if (contrastRatio('#111827', theme.pageBg) < 4.5)
    warnings.push(
      'Page text may be hard to read on the page background. Keep the page background light.',
    );
  return warnings;
};

// Small mock of the site so admins see the result before saving
function ThemePreview({ values }: { values: FormSite }) {
  const theme = resolveTheme(values);
  const items = [
    { label: 'Dashboard', icon: Home, active: true },
    { label: 'My Courses', icon: BookOpen, active: false },
    { label: 'Leaderboard', icon: Trophy, active: false },
  ];

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden flex h-72 text-xs shadow-sm">
      {/* Sidebar */}
      <div
        className="w-32 shrink-0 p-2 space-y-1 border-r border-black/5"
        style={{ backgroundColor: theme.sidebarBg }}
      >
        <div
          className="px-2 py-1.5 mb-2 font-semibold text-[11px] uppercase tracking-wide opacity-60"
          style={{ color: theme.sidebarText }}
        >
          Learning
        </div>
        {items.map(({ label, icon: Icon, active }) => (
          <div
            key={label}
            className="flex items-center gap-2 px-2 py-1.5 rounded-md font-medium"
            style={
              active
                ? {
                    color: theme.sidebarActive,
                    backgroundColor: `${theme.sidebarActive}26`,
                  }
                : { color: theme.sidebarText }
            }
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </div>
        ))}
      </div>

      {/* Page */}
      <div
        className="flex-1 p-3 space-y-3 overflow-hidden"
        style={{ backgroundColor: theme.pageBg }}
      >
        <div
          className="rounded-lg p-3 text-white"
          style={{ backgroundColor: theme.primary }}
        >
          <div className="font-semibold text-sm">Welcome back!</div>
          <div className="opacity-80">Continue where you left off</div>
        </div>
        <div className="bg-white rounded-lg p-3 border border-gray-100 space-y-2.5">
          <div className="flex gap-3 border-b border-gray-100">
            <span
              className="pb-1 border-b-2 font-medium"
              style={{ borderColor: theme.primary, color: theme.primary }}
            >
              In Progress
            </span>
            <span className="pb-1 text-gray-500">Completed</span>
          </div>
          <div className="font-medium text-gray-900">Data Analytics Basics</div>
          <div className="h-1.5 rounded-full bg-gray-100">
            <div
              className="h-1.5 rounded-full w-3/5"
              style={{ backgroundColor: theme.primary }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="underline" style={{ color: theme.primary }}>
              View details
            </span>
            <span
              className="px-2.5 py-1 rounded-md text-white font-medium"
              style={{ backgroundColor: theme.primary }}
            >
              Continue
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UICustomization() {
  usePermissionRouteAccess([
    BUSINESS_SETTINGS_PERMISSION.ADMIN_BUSINESS_SETTINGS_READ,
    BUSINESS_SETTINGS_PERMISSION.ADMIN_BUSINESS_SETTINGS_WRITE,
  ]);
  const canWrite = useHasPermission([
    BUSINESS_SETTINGS_PERMISSION.ADMIN_BUSINESS_SETTINGS_WRITE,
  ]);
  const queryClient = useQueryClient();
  const [site, setSite] = useState<Site>('learner');

  const { data, isLoading } = useQuery({
    queryKey: ['uiCustomization'],
    queryFn: () => uiCustomizationService.getUICustomization(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const formValues = useMemo<FormValues | undefined>(() => {
    if (!data) return undefined;
    const saved: UICustomizationData | undefined = data?.data;
    return {
      admin: toFormSite(saved?.admin),
      learner: toFormSite(saved?.learner),
      auth: toFormSite(saved?.auth),
    };
  }, [data]);

  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    values: formValues,
    mode: 'onChange',
  });

  const siteValues = toFormSite(watch(site));
  const warnings = contrastWarnings(resolveTheme(siteValues));

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) =>
      uiCustomizationService.saveUICustomization({
        admin: toPayloadSite(values.admin),
        learner: toPayloadSite(values.learner),
        auth: toPayloadSite(values.auth), 
      }),
    onSuccess: () => {
      toast.success('UI customization saved successfully!');
      queryClient.invalidateQueries({ queryKey: ['uiCustomization'] });
    },
    onError: (error: Error) => {
      toast.error(
        extractErrorMessage(error, 'Failed to save UI customization.'),
      );
    },
  });

  const applyValues = (values: FormSite) => {
    (Object.keys(EMPTY_SITE) as ThemeField[]).forEach((key) =>
      setValue(`${site}.${key}`, values[key], {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };

  const disabledProps = {
    disabled: !canWrite,
    title: !canWrite ? 'You do not have permission' : undefined,
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 rounded-xl p-6 text-white">
          <SkeletonLoader type="title" className="bg-white/20 mb-2" />
          <SkeletonLoader type="text" width="60%" className="bg-white/20" />
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <SkeletonLoader type="title" className="mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i}>
                <SkeletonLoader type="text" width="30%" className="mb-2" />
                <SkeletonLoader
                  type="custom"
                  className="h-10 w-full rounded-lg"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">UI Customization</h1>
        <p className="text-brand-blue-100">
          Brand the admin and learner sites with your colors.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(
          (values) => saveMutation.mutate(values),
          (errors) => {
            // Find which tab had an error and switch to it
            const invalidSite: Site = errors.admin
              ? 'admin'
              : errors.learner
                ? 'learner'
                : 'auth';
            const label =
              SITE_TABS.find((t) => t.key === invalidSite)?.label ||
              invalidSite;
            setSite(invalidSite);
            toast.error(`Please fix the invalid color in ${label}.`);
          },
        )}
        className="space-y-6"
      >
        <div className="card">
          <div className="flex items-center justify-between gap-4 mb-2">
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-brand-blue-600" />
              <h2 className="text-lg font-semibold">Theme</h2>
            </div>
            <button
              type="button"
              onClick={() => applyValues(EMPTY_SITE)}
              {...disabledProps}
              className={`inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
            >
              <RotateCcw className="w-4 h-4" />
              Reset to default
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Empty fields keep the default system look. Each tab is saved for
            its own site.
          </p>

          {/* Site tabs */}
          <div className="flex gap-2 border-b border-gray-200 mb-6">
            {SITE_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSite(tab.key)}
                className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                  site === tab.key
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
            {/* Settings */}
            <fieldset
              disabled={!canWrite}
              className={`xl:col-span-3 space-y-8 ${!canWrite ? 'opacity-60' : ''}`}
            >
              {/* Presets */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  Presets
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Start from a ready-made theme, then adjust any color.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {THEME_PRESETS.map((preset) => {
                    const swatch = resolveTheme(preset.theme);
                    return (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => applyValues(preset.theme)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-brand-blue-300 hover:bg-brand-blue-50 text-left text-sm"
                      >
                        <span className="flex -space-x-1 shrink-0">
                          {[
                            swatch.sidebarBg,
                            swatch.primary,
                            swatch.pageBg,
                          ].map((color, i) => (
                            <span
                              key={i}
                              className="w-4 h-4 rounded-full border border-white shadow-sm"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </span>
                        <span className="truncate">{preset.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Keyed by site so each tab renders its own controlled fields */}
              <div key={site} className="space-y-8">
                {COLOR_GROUPS
                  // Filter groups and fields if on auth site
                  .map((group) => ({
                    ...group,
                    fields: group.fields.filter((field) =>
                      site === 'auth'
                        ? field.key === 'primary_color' ||
                          field.key === 'page_background'
                        : true,
                    ),
                  }))
                  .filter((group) => group.fields.length > 0)
                  .map((group) => (
                    <div key={group.title}>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">
                        {group.title}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {group.fields.map((field) => {
                          const fieldName = `${site}.${field.key}` as const;
                          const value = watch(fieldName);
                          const fallback =
                            field.key === 'sidebar_active'
                              ? resolveTheme(siteValues).primary
                              : DEFAULT_SITE_THEME[field.key];
                          return (
                            <div
                              key={field.key}
                              className="flex items-start gap-3"
                            >
                              <div className="flex-1">
                                <Input
                                  name={fieldName}
                                  control={control}
                                  label={field.label}
                                  type="text"
                                  placeholder={`Default ${DEFAULT_SITE_THEME[field.key]}`}
                                  maxLength={7}
                                  helperText={field.description}
                                  className="font-mono"
                                  allowSpecialChars
                                  disableRTL
                                  rules={{
                                    validate: (v: string) =>
                                      !v ||
                                      HEX_COLOR.test(v) ||
                                      'Use a hex color like #1a2b3c',
                                  }}
                                />
                              </div>
                              <input
                                type="color"
                                value={validHex(value) || fallback}
                                onChange={(e) =>
                                  setValue(fieldName, e.target.value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                                className="mt-6 h-11 w-12 shrink-0 cursor-pointer rounded-lg border border-gray-200 bg-white p-1"
                                aria-label={`${field.label} picker`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            </fieldset>

            {/* Live preview */}
            <div className="xl:col-span-2">
              <div className="xl:sticky xl:top-24 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Preview{' '}
                  <span className="font-normal text-gray-500">
                    ({site === 'admin' ? 'Admin UI' : 'Learner UI'})
                  </span>
                </h3>
                <ThemePreview values={siteValues} />
                {warnings.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
                    {warnings.map((warning) => (
                      <p
                        key={warning}
                        className="flex items-start gap-2 text-xs text-amber-800"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        {warning}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className={`btn btn-primary inline-flex items-center${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canWrite || saveMutation.isPending}
            title={!canWrite ? 'You do not have permission' : undefined}
          >
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
